import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useSignIn, useClerk } from "@clerk/clerk-react";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import CoverLayout from "pages/authentication/components/CoverLayout";
import logoImage from "assets/images/logoCD.svg";
import styles from "../components/CoverLayout/styles";
import AuthApi from "api/auth";

function SignIn() {
  const classes = styles();
  const location = useLocation();
  const { signIn } = useSignIn();
  const { setActive } = useClerk();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (event) => {
    if (event) event.preventDefault();
    if (isLoading) return;

    if (!email?.trim()) {
      setError("Debe ingresar un correo electrónico.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Formato de correo electrónico inválido.");
      return;
    }

    if (!password) {
      setError("Debe ingresar una contraseña.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      // 1. Verificar estado del usuario
      const userStatus = await AuthApi.VerifyUserStatus(email);
      if (!userStatus.success) {
        throw new Error("Usuario no activo");
      }

      // 2. Login con Clerk
      const signInAttempt = await signIn.create({
        identifier: email,
        password,
      });

      // 3. Si el login es exitoso
      if (signInAttempt.status === "complete") {
        await setActive({ session: signInAttempt.createdSessionId });

        // 4. Redirección forzada
        const destination = location.state?.from || "/bienvenida";
        window.location.replace(destination);
      } else {
        throw new Error("Error en el proceso de login");
      }
    } catch (err) {
      if (err.message === "Usuario no activo") {
        setError("Tu cuenta está inactiva. Por favor, contacta al administrador.");
      } else if (err.errors?.[0]) {
        const clerkError = err.errors[0];
        switch (clerkError.code) {
          case "user_not_found":
            setError("Usuario no encontrado");
            break;
          case "invalid_credentials":
            setError("Credenciales inválidas");
            break;
          default:
            setError(clerkError.message || "Ha ocurrido un error");
        }
      } else {
        setError("Ha ocurrido un error al iniciar sesión");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CoverLayout
      header={
        <SuiBox display="flex" flexDirection="column">
          <div className={classes.logo}>
            <img src={logoImage} alt="Logo" />
          </div>
        </SuiBox>
      }
    >
      <SuiBox component="form" role="form" className={classes.form} onSubmit={handleLogin}>
        <SuiBox mb={3}>
          <SuiInput
            value={email}
            onChange={(e) => {
              setEmail(e.target.value.trim());
              setError("");
            }}
            type="email"
            placeholder="Correo electrónico"
            className={classes.input}
            size="large"
            disabled={isLoading}
          />
        </SuiBox>
        <SuiBox mb={3}>
          <SuiInput
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError("");
            }}
            type={showPassword ? "text" : "password"}
            placeholder="Contraseña"
            className={classes.input}
            withIcon={{
              icon: showPassword ? "visibility" : "visibility_off",
              direction: "right",
              color: "secondary",
              onClick: () => setShowPassword(!showPassword),
            }}
            size="large"
            disabled={isLoading}
          />
        </SuiBox>
        {error && (
          <SuiBox mb={3}>
            <SuiTypography variant="caption" color="error" textAlign="center" display="block">
              {error}
            </SuiTypography>
          </SuiBox>
        )}
        <SuiBox mt={2}>
          <SuiButton
            variant="gradient"
            buttonColor="dark"
            size="large"
            fullWidth
            onClick={handleLogin}
            disabled={isLoading}
            type="submit"
          >
            {isLoading ? "Iniciando sesión..." : "Ingresar"}
          </SuiButton>
        </SuiBox>
      </SuiBox>
    </CoverLayout>
  );
}

export default SignIn;
